/**
 * Tenant Management API Service
 * Handles all tenant-related API operations
 * Based on tenant-controller endpoints
 */

import { apiClient } from "../../../api/client";
import type {
  TenantDto,
  TenantDetailDto,
  CreateTenantDto,
  DuplicateTenantDto,
  UpdateTenantDto,
  UpdateTenantStatusDto,
  TenantListParams,
  PaginatedTenantResponse,
  TenantCategory,
  TenantListDto,
  TenantSubcategory,
} from "../types";

const BASE_URL = "/user-management/tenants";

/**
 * Tenant API Service Class
 * Provides methods for all tenant management operations
 */
export class TenantApiService {
  /**
   * Get list of tenants for dropdowns/selectors
   * GET /api/user-management/tenants/list
   */
  async getTenantsList(): Promise<TenantListDto[]> {
    const response = await apiClient.get<TenantListDto[]>(`${BASE_URL}/list`);
    return Array.isArray(response.data) ? response.data : [];
  }

  /**
   * Get paginated list of tenants with filtering and sorting
   * GET /api/user-management/tenants
   */
  async getTenants(
    params: TenantListParams = {},
  ): Promise<PaginatedTenantResponse> {
    const queryParams = new URLSearchParams();

    // Pagination (defaults)
    queryParams.append("page", (params.page || 1).toString());
    queryParams.append("pageSize", (params.pageSize || 20).toString());

    // Sorting
    if (params.sortBy) {
      queryParams.append("sortBy", params.sortBy);
    }
    if (params.sortDirection) {
      queryParams.append("sortDirection", params.sortDirection);
    }

    // Filters
    if (params.searchText) {
      queryParams.append("searchText", params.searchText);
    }
    if (params.status !== undefined) {
      queryParams.append("status", params.status.toString());
    }

    const response = await apiClient.get<PaginatedTenantResponse>(
      `${BASE_URL}?${queryParams.toString()}`,
    );

    // Handle both paginated and array responses
    if (Array.isArray(response.data)) {
      return {
        data: response.data,
        page: params.page || 1,
        pageSize: params.pageSize || 20,
        totalElements: response.data.length,
        totalPages: 1,
      };
    }

    return response.data;
  }

  /**
   * Get tenant details by ID
   * GET /api/user-management/tenants/{id}
   */
  async getTenantById(tenantId: number): Promise<TenantDetailDto> {
    const response = await apiClient.get<TenantDetailDto>(
      `${BASE_URL}/${tenantId}`,
    );
    return response.data;
  }

  /**
   * Create a new tenant
   * POST /api/user-management/tenants (multipart/form-data)
   * Parts: tenant (JSON blob), logo (optional image file)
   */
  async createTenant(
    tenantData: CreateTenantDto,
    logoFile?: File | null,
  ): Promise<TenantDto> {
    const fd = new FormData();
    fd.append(
      "tenant",
      new Blob([JSON.stringify(tenantData)], { type: "application/json" }),
    );
    if (logoFile) {
      fd.append("logo", logoFile);
    }
    const response = await apiClient.post<TenantDto>(BASE_URL, fd);
    return response.data;
  }

  /**
   * Duplicate an existing tenant
   * POST /api/user-management/tenants/{id}/duplicate
   * Copies roles, subcategories, questionnaire answers, subscription, and profile from the source.
   * Only name, admin credentials, and contact are required from the caller.
   */
  async duplicateTenant(
    sourceId: number,
    data: DuplicateTenantDto,
  ): Promise<TenantDto> {
    const response = await apiClient.post<TenantDto>(
      `${BASE_URL}/${sourceId}/duplicate`,
      data,
    );
    return response.data;
  }

  /**
   * Update existing tenant
   * PUT /api/user-management/tenants/{id} (multipart/form-data)
   * Parts: tenant (JSON blob), logo (optional image file — replaces existing logo)
   */
  async updateTenant(
    tenantId: number,
    tenantData: UpdateTenantDto,
    logoFile?: File | null,
  ): Promise<TenantDto> {
    const fd = new FormData();
    fd.append(
      "tenant",
      new Blob([JSON.stringify(tenantData)], { type: "application/json" }),
    );
    if (logoFile) {
      fd.append("logo", logoFile);
    }
    const response = await apiClient.put<TenantDto>(
      `${BASE_URL}/${tenantId}`,
      fd,
    );
    return response.data;
  }

  /**
   * Get a short-lived presigned URL for displaying the tenant's logo
   * GET /api/user-management/tenants/{id}/logo-url
   */
  async getTenantLogoUrl(tenantId: number): Promise<string> {
    const response = await apiClient.get<{ logoUrl: string }>(
      `${BASE_URL}/${tenantId}/logo-url`,
    );
    return response.data.logoUrl;
  }

  /**
   * Delete tenant
   * DELETE /api/user-management/tenants/{id}
   */
  async deleteTenant(tenantId: number): Promise<TenantDto> {
    const response = await apiClient.delete<TenantDto>(
      `${BASE_URL}/${tenantId}`,
    );
    return response.data;
  }

  /**
   * Update tenant status
   * PATCH /api/user-management/tenants/{id}/status
   */
  async updateTenantStatus(
    tenantId: number,
    statusData: UpdateTenantStatusDto,
  ): Promise<TenantDto> {
    const response = await apiClient.patch<TenantDto>(
      `${BASE_URL}/${tenantId}/status`,
      statusData,
    );
    return response.data;
  }

  /**
   * Get tenant categories
   * GET /api/user-management/tenants/categories
   */
  async getTenantCategories(): Promise<TenantCategory[]> {
    try {
      const response = await apiClient.get<TenantCategory[]>(
        `${BASE_URL}/categories`,
      );
      return response.data || [];
    } catch (error) {
      console.warn("Failed to fetch tenant categories:", error);
      return [];
    }
  }

  /**
   * Get all subcategories (global list)
   * GET /api/user-management/tenants/subcategories
   */
  async getAllSubcategories(): Promise<TenantSubcategory[]> {
    const response = await apiClient.get<TenantSubcategory[]>(
      `${BASE_URL}/subcategories`,
    );
    return Array.isArray(response.data) ? response.data : [];
  }

  /**
   * Get subcategories assigned to a tenant
   * GET /api/user-management/tenants/{id}/tenant-subcategories
   */
  async getTenantSubcategories(tenantId: number): Promise<TenantSubcategory[]> {
    const response = await apiClient.get<TenantSubcategory[]>(
      `${BASE_URL}/${tenantId}/tenant-subcategories`,
    );
    return Array.isArray(response.data) ? response.data : [];
  }

  /**
   * Get subcategories available to add to a tenant (not yet assigned)
   * GET /api/user-management/tenants/{id}/tenant-subcategories/available
   */
  async getAvailableSubcategories(
    tenantId: number,
  ): Promise<TenantSubcategory[]> {
    const response = await apiClient.get<TenantSubcategory[]>(
      `${BASE_URL}/${tenantId}/tenant-subcategories/available`,
    );
    return Array.isArray(response.data) ? response.data : [];
  }

  /**
   * Add subcategories to a tenant
   * POST /api/user-management/tenants/{id}/tenant-subcategories
   */
  async addTenantSubcategories(
    tenantId: number,
    subcategoryIds: number[],
  ): Promise<TenantSubcategory[]> {
    const response = await apiClient.post<TenantSubcategory[]>(
      `${BASE_URL}/${tenantId}/tenant-subcategories`,
      { subcategoryIds },
    );
    return Array.isArray(response.data) ? response.data : [];
  }

  /**
   * Remove subcategories from a tenant
   * DELETE /api/user-management/tenants/{id}/tenant-subcategories
   */
  async removeTenantSubcategories(
    tenantId: number,
    subcategoryIds: number[],
  ): Promise<TenantSubcategory[]> {
    const response = await apiClient.delete<TenantSubcategory[]>(
      `${BASE_URL}/${tenantId}/tenant-subcategories`,
      { body: JSON.stringify({ subcategoryIds }) },
    );
    return Array.isArray(response.data) ? response.data : [];
  }

  /**
   * Activate a tenant
   * Sets status to Active (1)
   */
  async activateTenant(tenantId: number): Promise<TenantDto> {
    return this.updateTenantStatus(tenantId, { status: 1 });
  }

  /**
   * Deactivate a tenant
   * Sets status to Inactive (0)
   */
  async deactivateTenant(tenantId: number): Promise<TenantDto> {
    return this.updateTenantStatus(tenantId, { status: 0 });
  }

  /**
   * Suspend a tenant
   * Sets status to Suspended (2)
   */
  async suspendTenant(tenantId: number): Promise<TenantDto> {
    return this.updateTenantStatus(tenantId, { status: 2 });
  }

  /**
   * Trigger on-demand compliance profile generation
   * POST /api/user-management/tenants/{id}/generate-profile
   * Requires TENANT_UPDATE permission. Returns after profile is saved (~5-10s).
   */
  async generateProfile(tenantId: number): Promise<void> {
    await apiClient.post<null>(`${BASE_URL}/${tenantId}/generate-profile`, {});
  }
}

// Export singleton instance
export const tenantApi = new TenantApiService();
